import { z } from "zod";

import {
  RUN_STATUSES,
  type InvoiceReviewFilters,
} from "../invoice-reviews/types.ts";

/**
 * La URL de la página maestra de facturas (`/work/facturas`): qué vista se
 * mira y, en la lista, sus filtros. Todo en la URL, como el mes (`?p`):
 * recargar no lo pierde y el enlace que se comparte enseña lo mismo.
 *
 * Lo que no se entiende se descarta (`catch`) en vez de llevar a la pantalla
 * de error: un filtro mal copiado no es motivo para no ver la página.
 */

/**
 * Las vistas: el Resumen («¿cómo vamos?»), la lista de facturas y los dos
 * detalles técnicos que antes eran páginas aparte.
 */
export const INVOICE_VIEWS = [
  "resumen",
  "facturas",
  "proceso",
  "acierto",
] as const;

export type InvoiceView = (typeof INVOICE_VIEWS)[number];

/**
 * La decisión final, como la filtra `list_invoice_reviews`: `none` es «sin
 * decidir» y `decided`, cualquiera de las tres (`in`).
 */
export const DECISION_FILTERS = [
  "valid",
  "invalid",
  "for_data",
  "none",
  "decided",
] as const;

export type DecisionFilter = (typeof DECISION_FILTERS)[number];

/** El router lee como número lo que parece un número: aquí todo es texto. */
const text = z
  .union([z.string(), z.number()])
  .transform((value) => String(value))
  .refine((value) => value !== "")
  .optional()
  .catch(undefined);

/** Un instante ISO 8601, como lo manda el API (`today.from`). */
const instant = z
  .string()
  .refine((value) => !Number.isNaN(Date.parse(value)))
  .optional()
  .catch(undefined);

export const invoicesSearchSchema = z.object({
  vista: z.enum(INVOICE_VIEWS).optional().catch(undefined),
  status: z.enum(RUN_STATUSES).optional().catch(undefined),
  /** El tipo de foto: un resultado de «Identificar qué es la foto». */
  photoType: text,
  /** El paso en el que está ahora. */
  step: text,
  /** El buscador de Digitación: `dep:`, `farmacia:`, `fac:`, `#id`, `subida:`… */
  q: z.union([z.string(), z.number()]).optional().catch(undefined),
  /** Buscar una factura por su número (enlaces de antes; ahora es `#id` en `q`). */
  snap: z.coerce.number().int().positive().optional().catch(undefined),
  /** La decisión final. */
  decision: z.enum(DECISION_FILTERS).optional().catch(undefined),
  /** Quién decidió: su id de auth. */
  decidedBy: text,
  /** Su nombre, solo para decirlo en el filtro. */
  decidedByName: text,
  /** Decididas desde (incluido) y hasta (sin incluir): un día en Ecuador. */
  decidedFrom: instant,
  decidedTo: instant,
  /** Por qué falló (`FAILURE_REASONS`): «Por el límite de la IA». */
  motivo: text,
  /**
   * `no`: sin las viejas que decidió lala4 (`decidedInLala4`), de cuando OC
   * corría «En paralelo»; lo que es trabajo de este sistema. «Con problema»
   * es `status=failed` y `lala4=no`, y el total del mes, `lala4=no`. No se
   * elige en la interfaz: desde el corte ya no hay filtro «Ya decidida en
   * lala4», y el `si` de un enlace viejo se ignora.
   */
  lala4: z.enum(["no"]).optional().catch(undefined),
});

export type InvoicesSearch = z.infer<typeof invoicesSearchSchema>;

/** Los filtros de la lista, por su nombre en la URL. */
const LIST_KEYS = [
  "status",
  "photoType",
  "step",
  "snap",
  "q",
  "decision",
  "decidedBy",
  "decidedFrom",
  "decidedTo",
  "motivo",
  "lala4",
] as const satisfies readonly (keyof InvoicesSearch)[];

/** Sin filtros de la lista: lo que se borra con «Quitar filtros». */
export const NO_LIST_FILTERS = Object.fromEntries(
  [...LIST_KEYS, "decidedByName"].map((key) => [key, undefined]),
) as Partial<InvoicesSearch>;

/**
 * La vista que se mira. Sin `vista`, el Resumen; pero un enlace de antes
 * --`/work/facturas?status=waiting&photoType=kardex`-- abre la lista con
 * esos filtros, que es lo que abría.
 */
export const viewOf = (search: InvoicesSearch): InvoiceView =>
  search.vista ??
  (LIST_KEYS.some((key) => search[key] !== undefined) ? "facturas" : "resumen");

/** Lo escrito en el buscador, como texto y sin vacíos. */
export const queryOf = (search: InvoicesSearch): string | undefined => {
  const text = search.q === undefined ? "" : String(search.q).trim();
  return text === "" ? undefined : text;
};

/**
 * Los filtros de la lista: los de la URL y el mes del panel (`?p`, la
 * bolsa). Las decididas un día van SIN mes: lo del día es de cualquier
 * bolsa (`contrato-progreso.md`, §1).
 */
export const listFiltersOf = (
  search: InvoicesSearch,
  period: string,
): InvoiceReviewFilters => {
  const typed = queryOf(search);
  return {
    ...(search.status !== undefined ? { status: search.status } : {}),
    ...(search.photoType !== undefined ? { photoType: search.photoType } : {}),
    ...(search.step !== undefined ? { stepKey: search.step } : {}),
    ...(search.snap !== undefined ? { snapId: search.snap } : {}),
    ...(typed === undefined ? {} : { q: typed }),
    ...(search.decision !== undefined ? { decision: search.decision } : {}),
    ...(search.decidedBy !== undefined ? { decidedBy: search.decidedBy } : {}),
    ...(search.decidedFrom !== undefined
      ? { decidedFrom: search.decidedFrom }
      : {}),
    ...(search.decidedTo !== undefined ? { decidedTo: search.decidedTo } : {}),
    ...(search.motivo !== undefined ? { failureReason: search.motivo } : {}),
    ...(search.lala4 === "no" ? { decidedInLala4: false } : {}),
    /* Buscar por número es por identificador, no por bolsa: encuentra la
     factura sea del mes que sea. */
    ...(search.decidedFrom === undefined &&
    search.decidedTo === undefined &&
    search.snap === undefined &&
    typed === undefined
      ? { period }
      : {}),
  };
};
